import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import PennText from "@/components/ui/text/PennText";
import { CustomizeAssumptions } from "@/components/popup/profile/CustomizeAssumptions";
import { PennButton } from "@/components/ui/button/PennButton";
import type { ApplicationDetail, ApplicationSummary } from "@/api/applications";
import {
  computePortfolioAggregates,
  formatAggregateMetric,
} from "@/utils/portfolio-aggregates";
import { useAppSelector } from "@/stores/hooks";
import { useRefreshProfile } from "@penn/web-common";
import { assumptionsDtoToForm } from "@/utils/assumptions-mapper";
import { readStoredWorkFor } from "@penn/web-common";

type ApplicationParametersProps = {
  selectedApps?: ApplicationSummary[];
  /** Full application rows (fastener qty) — same fetch as filter-driven brands. */
  selectedAppDetails?: ApplicationDetail[];
  isAuthenticated: boolean;
  onAddExisting?: () => void;
  onNewApp?: () => void;
  onEditApp?: (app: ApplicationSummary) => void;
  onRemoveApp?: (applicationId: string) => void;
  onClearSelection?: () => void;
};

function formatAnnualVolume(vol: number | null): string {
  if (vol == null || !Number.isFinite(vol)) return "Annual volume: —";
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(vol)} pcs/year`;
}

export const ApplicationParameters = ({
  selectedApps = [],
  selectedAppDetails = [],
  isAuthenticated,
  onAddExisting,
  onNewApp,
  onEditApp,
  onRemoveApp,
  onClearSelection,
}: ApplicationParametersProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showAssumptions, setShowAssumptions] = useState(false);
  const assumptions = useAppSelector((s) => s.auth.assumptions);
  const refreshProfile = useRefreshProfile();
  const workForOther = readStoredWorkFor().kind === "other";

  // Same gate as "Add new/existing application" (FilterPage's `requireAuth`) —
  // this popup edits real profile/account data, so it needs a signed-in user.
  const handleCustomizeAssumptions = () => {
    if (!isAuthenticated) {
      navigate("/login", { state: { from: { pathname: location.pathname } } });
      return;
    }
    setShowAssumptions(true);
  };

  const portfolio = useMemo(
    () => computePortfolioAggregates(selectedAppDetails),
    [selectedAppDetails],
  );

  return (
    <>
      <section id="application-parameters" className="pb-6 scroll-mt-28">
        <div className="w-full max-w-[1600px] mx-auto px-3 sm:px-4 lg:px-6 2xl:px-8 mt-8 mb-3 text-center">
        <PennText as="h2" size="2xl" color="on-navy" font="semibold" align="center">
          Enter your application parameters
        </PennText>
        <PennText as="p" size="base" color="on-navy-secondary" align="center" className="mt-1">
          Define one or more applications to generate indicative cost and utilization KPIs.
        </PennText>
        </div>

        <div className="w-full max-w-[1600px] mx-auto px-3 sm:px-4 lg:px-6 2xl:px-8">
        <div className="bg-white border border-gray-200 rounded-2xl shadow-soft p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-3">
          <PennText as="h3" size="lg" color="blue_grey1" font="semibold">
            Applications
            {selectedApps.length > 0 ? ` (${selectedApps.length})` : ""}
          </PennText>
          <PennText as="p" size="xs" color="blue_grey" className="sm:text-right max-w-xs">
            Define one or more parts to evaluate a solution mix.
          </PennText>
        </div>

        <div className="flex flex-wrap gap-3 mb-5">
          <PennButton variant="primary" size="sm" onClick={onNewApp}>
            Add new application
          </PennButton>
          <PennButton variant="secondary" size="sm" onClick={onAddExisting}>
            Add existing application
          </PennButton>
        </div>

        {selectedApps.length === 0 ? (
          <div className="mb-5 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-4">
            <PennText as="p" size="sm" color="blue_grey">
              No applications yet. Click{" "}
              <span className="font-semibold text-blue_grey1">Add new application</span> or{" "}
              <span className="font-semibold text-blue_grey1">Add existing application</span> to
              define part and fastener parameters.
            </PennText>
          </div>
        ) : (
          <div
            className={`mb-5 space-y-3 ${selectedApps.length > 1 ? "max-h-72 overflow-y-auto pr-1" : ""}`}
          >
            {selectedApps.map((app, idx) => {
              const title =
                app.name?.trim() || app.applicationNumber?.trim() || `Application / Part ${idx + 1}`;
              const appNo = app.applicationNumber?.trim()
                ? `App No.: ${app.applicationNumber}`
                : "";
              const vol = formatAnnualVolume(app.annualVolume);

              return (
                <div
                  key={app.applicationId}
                  className="flex items-center justify-between gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-soft hover:bg-gray-50 cursor-pointer"
                  role="button"
                  tabIndex={0}
                  onClick={() => onEditApp?.(app)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onEditApp?.(app);
                    }
                  }}
                >
                  <div className="min-w-0 flex-1">
                    <PennText as="p" size="sm" color="blue_grey1" font="semibold" className="truncate">
                      {title}
                    </PennText>
                    <PennText as="p" size="xs" color="blue_grey" className="truncate mt-0.5">
                      {[appNo, vol].filter(Boolean).join(" · ")}
                    </PennText>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditApp?.(app);
                      }}
                      className="inline-flex items-center rounded-xl border border-penn-400 bg-penn-100 px-2.5 py-1 text-small-medium text-penn-700 hover:bg-penn-400/30 transition-colors cursor-pointer"
                    >
                      Edit
                    </button>
                    <PennButton
                      variant="primary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveApp?.(app.applicationId);
                      }}
                    >
                      Remove
                    </PennButton>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="border border-gray-200 rounded-2xl p-5 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <PennText as="p" size="xs" color="blue_grey2" font="medium" className="uppercase mb-3">
                Portfolio totals
              </PennText>
              <div className="grid grid-cols-2 gap-4">
                <MetricCell
                  label="Total annual volume"
                  value={formatAggregateMetric(portfolio?.totalAnnualVolume)}
                />
                <MetricCell
                  label="Total fasteners / year"
                  value={formatAggregateMetric(portfolio?.totalFastenersPerYear)}
                />
              </div>
            </div>

            <div>
              <PennText as="p" size="xs" color="blue_grey2" font="medium" className="uppercase mb-3">
                Derived inputs
              </PennText>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <MetricCell
                  label="Fasteners / part (avg.)"
                  value={formatAggregateMetric(portfolio?.avgFastenersPerPart, 1)}
                />
                <MetricCell
                  label="Element types / part (avg.)"
                  value={formatAggregateMetric(portfolio?.avgElementTypesPerPart, 1)}
                />
                <MetricCell
                  label="Parts / year"
                  value={formatAggregateMetric(portfolio?.partsPerYear)}
                />
              </div>
              {selectedApps.length > 0 && selectedAppDetails.length < selectedApps.length && (
                <PennText as="p" size="xs" color="blue_grey" className="mt-2">
                  Loading portfolio totals…
                </PennText>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <PennText as="p" size="xs" color="blue_grey" font="semibold">
              Actions
            </PennText>
            <PennText as="p" size="xs" color="blue_grey" className="mt-0.5">
              Selected applications drive filters and comparison cost calculations.
            </PennText>
          </div>
          <div className="flex flex-wrap gap-3">
            <PennButton
              variant="secondary"
              size="sm"
              disabled={selectedApps.length === 0}
              onClick={onClearSelection}
            >
              Clear selection
            </PennButton>
            <PennButton variant="secondary" size="sm" onClick={handleCustomizeAssumptions}>
              Customize assumptions
            </PennButton>
          </div>
        </div>
        </div>
        </div>
      </section>

      {showAssumptions && (
        <CustomizeAssumptions
          onClose={() => setShowAssumptions(false)}
          onSaved={() => void refreshProfile()}
          initialValues={assumptionsDtoToForm(assumptions)}
          readOnly={workForOther}
        />
      )}
    </>
  );
};

const MetricCell = ({ label, value }: { label: string; value: string }) => (
  <div>
    <PennText as="p" size="xs" color="blue_grey" className="leading-tight">
      {label}
    </PennText>
    <PennText as="p" size="base" color="blue_grey1" font="semibold" className="mt-0.5">
      {value}
    </PennText>
  </div>
);
